import React, { useEffect, useMemo, useRef, useState } from 'react'
import { UseMutationResult } from '@tanstack/react-query'

import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Modal,
    Paper,
    Radio,
    RadioGroup,
    Slide,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    Email as EmailIcon,
    Sms as SmsIcon,
    Inbox as InboxIcon,
    Search as SearchIcon,
    People as PeopleIcon,
    Person as PersonIcon,
    History as HistoryIcon,
    Send as SendIcon,
} from '@material-ui/icons'
import { InputAdornment } from '@material-ui/core'

import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import {
    AudienceType,
    LeaseScope,
    AudiencePreviewResponse,
    SelectableTenant,
    TenantMessage,
} from '../../models/TenantMessage'
import {
    DraftMessageResponse,
    SendEventMessageInput,
} from '../../models/PropertyEvent'
import {
    useTenantMessages,
    useTenantMessageTenants,
} from '../../hooks/useTenantMessages'

const BORDER = '#e6e9ed'
const SURFACE = '#f6f8fa'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: 16,
        overflow: 'hidden',
        outline: 'none',
        boxShadow:
            '0 24px 48px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04)',
        width: '90vw',
        [theme.breakpoints.up('sm')]: {
            width: '80vw',
            maxWidth: 720,
        },
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: '#fff',
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        backgroundColor: SURFACE,
        padding: theme.spacing(2.5),
    },
    section: {
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(1.5),
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${BORDER}`,
        backgroundColor: '#fff',
    },
    loriButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
    },
    primaryButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        boxShadow: 'none',
    },
    cancelButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
    },
    audienceCard: {
        flex: 1,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: theme.spacing(1.5),
        cursor: 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
            borderColor: '#b0b8c1',
        },
    },
    audienceCardActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}08`,
    },
    scopeOption: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 0),
    },
    tenantList: {
        maxHeight: 220,
        overflowY: 'auto',
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        marginTop: theme.spacing(1.5),
    },
    tenantRow: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.75, 1.5),
        borderBottom: `1px solid ${BORDER}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
}))

const SCOPE_OPTIONS: Array<{
    value: LeaseScope
    label: string
    description: string
    icon: JSX.Element
}> = [
    {
        value: 'CURRENT',
        label: 'Current & Notice',
        description: 'Tenants with an active or ending lease',
        icon: <PeopleIcon fontSize="small" />,
    },
    {
        value: 'CURRENT_AND_FUTURE',
        label: 'Current, Notice & Future',
        description: 'Includes upcoming move-ins',
        icon: <PersonIcon fontSize="small" />,
    },
    {
        value: 'FUTURE',
        label: 'Future',
        description: 'Only tenants with an upcoming lease',
        icon: <HistoryIcon fontSize="small" />,
    },
]

const LEASE_STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: 'Applicant', color: '#9ca3af' },
    3: { label: 'Future', color: '#8b5cf6' },
    4: { label: 'Current', color: '#10b981' },
    5: { label: 'Notice', color: '#f59e0b' },
    6: { label: 'Past', color: '#6b7280' },
}

interface EventAnnouncementModalProps {
    open: boolean
    onClose: () => void
    eventId: number
    eventTitle: string
    draftMessage: UseMutationResult<DraftMessageResponse, unknown, number>
    sendEventMessage: UseMutationResult<
        TenantMessage,
        unknown,
        SendEventMessageInput & { id: number }
    >
    onSent?: (message: TenantMessage) => void
}

const generateToken = (): string =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })

export const EventAnnouncementModal: React.FC<EventAnnouncementModalProps> = ({
    open,
    onClose,
    eventId,
    eventTitle,
    draftMessage,
    sendEventMessage,
    onSent,
}) => {
    const classes = useStyles()
    const { audiencePreview } = useTenantMessages()
    const { tenants, isLoading: tenantsLoading } = useTenantMessageTenants()

    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [audienceType, setAudienceType] = useState<AudienceType>('ALL')
    const [leaseScope, setLeaseScope] = useState<LeaseScope>('CURRENT')
    const [selectedLeaseIds, setSelectedLeaseIds] = useState<number[]>([])
    const [searchText, setSearchText] = useState('')
    const [sendEmail, setSendEmail] = useState(false)
    const [sendSms, setSendSms] = useState(false)
    const [emailSenderName, setEmailSenderName] = useState('')
    const [emailReplyTo, setEmailReplyTo] = useState('')
    const [clientToken, setClientToken] = useState('')
    const [
        previewInfo,
        setPreviewInfo,
    ] = useState<AudiencePreviewResponse | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (open) {
            setSubject('')
            setBody('')
            setAudienceType('ALL')
            setLeaseScope('CURRENT')
            setSelectedLeaseIds([])
            setSearchText('')
            setSendEmail(false)
            setSendSms(false)
            setEmailSenderName('')
            setEmailReplyTo('')
            setClientToken(generateToken())
            setPreviewInfo(null)
            setConfirmOpen(false)
        }
    }, [open])

    useEffect(() => {
        if (!open || (!sendEmail && !sendSms)) {
            setPreviewInfo(null)
            return
        }
        if (previewTimerRef.current) {
            clearTimeout(previewTimerRef.current)
        }
        previewTimerRef.current = setTimeout(() => {
            audiencePreview.mutate(
                {
                    audience_type: audienceType,
                    lease_scope:
                        audienceType === 'ALL' ? leaseScope : undefined,
                    lease_ids:
                        audienceType === 'SELECTED' ? selectedLeaseIds : [],
                },
                {
                    onSuccess: (data) => setPreviewInfo(data),
                },
            )
        }, 400)
        return () => {
            if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
        }
    }, [open, sendEmail, sendSms, audienceType, leaseScope, selectedLeaseIds])

    const filteredTenants = useMemo(() => {
        return tenants.filter((t) => {
            if (!searchText.trim()) return true
            const q = searchText.toLowerCase()
            return (
                t.name.toLowerCase().includes(q) ||
                t.unit_label.toLowerCase().includes(q) ||
                t.email.toLowerCase().includes(q)
            )
        })
    }, [tenants, searchText])

    const canSend =
        subject.trim().length > 0 &&
        body.trim().length > 0 &&
        (audienceType === 'ALL' || selectedLeaseIds.length > 0)

    const isDrafting = draftMessage.isLoading
    const isSending = sendEventMessage.isLoading

    const handleDraftWithLori = () => {
        draftMessage.mutate(eventId, {
            onSuccess: (data) => {
                setSubject(data.subject.slice(0, 255))
                setBody(data.body)
            },
        })
    }

    const confirmSend = () => {
        setConfirmOpen(false)
        sendEventMessage.mutate(
            {
                id: eventId,
                subject: subject.trim(),
                body: body.trim(),
                audience_type: audienceType,
                lease_scope: audienceType === 'ALL' ? leaseScope : undefined,
                lease_ids: audienceType === 'SELECTED' ? selectedLeaseIds : [],
                client_token: clientToken,
                send_email: sendEmail,
                send_sms: sendSms,
                email_sender_name:
                    sendEmail && emailSenderName.trim()
                        ? emailSenderName.trim()
                        : undefined,
                email_reply_to:
                    sendEmail && emailReplyTo.trim()
                        ? emailReplyTo.trim()
                        : undefined,
            },
            {
                onSuccess: (data) => {
                    onSent?.(data)
                    onClose()
                },
            },
        )
    }

    const toggleTenant = (tenant: SelectableTenant) => {
        setSelectedLeaseIds((prev) =>
            prev.includes(tenant.lease_id)
                ? prev.filter((id) => id !== tenant.lease_id)
                : [...prev, tenant.lease_id],
        )
    }

    return (
        <>
            <Modal
                open={open}
                onClose={(_e: object, reason: string) => {
                    if (
                        reason === 'backdropClick' &&
                        (isDrafting || isSending)
                    ) {
                        return
                    }
                    onClose()
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper className={classes.paper}>
                        <Box className={classes.header}>
                            <Box className={classes.headerIcon}>
                                <SendIcon />
                            </Box>
                            <Box flex={1}>
                                <Typography
                                    style={{
                                        fontWeight: 700,
                                        fontSize: '1.05rem',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Send Announcement
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: '0.8rem',
                                        color: '#6b7280',
                                    }}
                                >
                                    Announce &ldquo;{eventTitle}&rdquo; to
                                    residents
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box className={classes.body}>
                            <Box className={classes.section}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    marginBottom="12px"
                                >
                                    <Typography
                                        className={classes.sectionTitle}
                                        style={{ marginBottom: 0 }}
                                    >
                                        Message
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.loriButton}
                                        startIcon={
                                            isDrafting ? (
                                                <CircularProgress
                                                    size={16}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <LoriIcon
                                                    width={18}
                                                    height={18}
                                                    color="#008C85"
                                                />
                                            )
                                        }
                                        onClick={handleDraftWithLori}
                                        disabled={isDrafting || isSending}
                                    >
                                        {isDrafting
                                            ? 'Lori is writing…'
                                            : 'Draft with Lori'}
                                    </Button>
                                </Box>

                                <TextField
                                    label="Subject"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={subject}
                                    onChange={(e) =>
                                        setSubject(e.target.value.slice(0, 255))
                                    }
                                    inputProps={{ maxLength: 255 }}
                                    helperText={`${subject.length}/255`}
                                    style={{ marginBottom: 12 }}
                                />
                                <TextField
                                    label="Body"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Enter your announcement to residents..."
                                />

                                <Box mt={2}>
                                    <Typography
                                        variant="caption"
                                        style={{
                                            fontWeight: 600,
                                            display: 'block',
                                            marginBottom: 8,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            fontSize: '0.625rem',
                                        }}
                                    >
                                        Delivery Channels
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked
                                                disabled
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 6 }}
                                            >
                                                <InboxIcon
                                                    style={{
                                                        fontSize: 16,
                                                        color: '#008C85',
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontSize: '0.8125rem',
                                                    }}
                                                >
                                                    Portal (always on)
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={sendEmail}
                                                    onChange={(e) =>
                                                        setSendEmail(
                                                            e.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 6 }}
                                                >
                                                    <EmailIcon
                                                        style={{
                                                            fontSize: 16,
                                                            color: sendEmail
                                                                ? '#008C85'
                                                                : '#9ca3af',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontSize:
                                                                '0.8125rem',
                                                        }}
                                                    >
                                                        Also send via Email
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        {sendEmail && previewInfo && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                ({previewInfo.email_reachable}{' '}
                                                of {previewInfo.count}{' '}
                                                reachable)
                                            </Typography>
                                        )}
                                    </Box>
                                    {sendEmail && (
                                        <Box
                                            display="flex"
                                            style={{ gap: 12, marginLeft: 32 }}
                                        >
                                            <TextField
                                                label="Sender Name"
                                                variant="outlined"
                                                size="small"
                                                value={emailSenderName}
                                                onChange={(e) =>
                                                    setEmailSenderName(
                                                        e.target.value.slice(
                                                            0,
                                                            255,
                                                        ),
                                                    )
                                                }
                                                style={{ flex: 1 }}
                                            />
                                            <TextField
                                                label="Reply-To Email"
                                                variant="outlined"
                                                size="small"
                                                type="email"
                                                value={emailReplyTo}
                                                onChange={(e) =>
                                                    setEmailReplyTo(
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ flex: 1 }}
                                            />
                                        </Box>
                                    )}
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={sendSms}
                                                    onChange={(e) =>
                                                        setSendSms(
                                                            e.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 6 }}
                                                >
                                                    <SmsIcon
                                                        style={{
                                                            fontSize: 16,
                                                            color: sendSms
                                                                ? '#008C85'
                                                                : '#9ca3af',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontSize:
                                                                '0.8125rem',
                                                        }}
                                                    >
                                                        Also send via SMS
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        {sendSms && previewInfo && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                ({previewInfo.sms_reachable} of{' '}
                                                {previewInfo.count} reachable)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                className={classes.section}
                                style={{ marginBottom: 0 }}
                            >
                                <Typography className={classes.sectionTitle}>
                                    Recipients
                                </Typography>

                                <Box display="flex" style={{ gap: 12 }}>
                                    <Box
                                        className={`${classes.audienceCard} ${
                                            audienceType === 'ALL'
                                                ? classes.audienceCardActive
                                                : ''
                                        }`}
                                        onClick={() => setAudienceType('ALL')}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                        >
                                            All tenants
                                        </Typography>
                                        <Typography
                                            style={{
                                                fontSize: '0.7rem',
                                                color: '#6b7280',
                                            }}
                                        >
                                            By lease scope
                                        </Typography>
                                    </Box>
                                    <Box
                                        className={`${classes.audienceCard} ${
                                            audienceType === 'SELECTED'
                                                ? classes.audienceCardActive
                                                : ''
                                        }`}
                                        onClick={() =>
                                            setAudienceType('SELECTED')
                                        }
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                        >
                                            Selected tenants
                                        </Typography>
                                        <Typography
                                            style={{
                                                fontSize: '0.7rem',
                                                color: '#6b7280',
                                            }}
                                        >
                                            Pick specific leases
                                        </Typography>
                                    </Box>
                                </Box>

                                {audienceType === 'ALL' && (
                                    <RadioGroup
                                        value={leaseScope}
                                        onChange={(e) =>
                                            setLeaseScope(
                                                e.target.value as LeaseScope,
                                            )
                                        }
                                        style={{ marginTop: 12 }}
                                    >
                                        {SCOPE_OPTIONS.map((opt) => (
                                            <FormControlLabel
                                                key={opt.value}
                                                value={opt.value}
                                                control={
                                                    <Radio
                                                        size="small"
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    <Box
                                                        className={
                                                            classes.scopeOption
                                                        }
                                                    >
                                                        {opt.icon}
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                style={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {opt.label}
                                                            </Typography>
                                                            <Typography
                                                                style={{
                                                                    fontSize:
                                                                        '0.7rem',
                                                                    color:
                                                                        '#6b7280',
                                                                }}
                                                            >
                                                                {
                                                                    opt.description
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                )}

                                {audienceType === 'SELECTED' && (
                                    <Box mt={1.5}>
                                        <TextField
                                            placeholder="Search tenants..."
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(e.target.value)
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon
                                                            style={{
                                                                fontSize: 20,
                                                                color:
                                                                    '#9ca3af',
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{
                                                display: 'block',
                                                marginTop: 8,
                                            }}
                                        >
                                            {selectedLeaseIds.length} selected
                                        </Typography>
                                        <Box className={classes.tenantList}>
                                            {tenantsLoading ? (
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    p={2}
                                                >
                                                    <CircularProgress
                                                        size={24}
                                                    />
                                                </Box>
                                            ) : filteredTenants.length === 0 ? (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{ padding: 16 }}
                                                >
                                                    No tenants found
                                                </Typography>
                                            ) : (
                                                filteredTenants.map(
                                                    (tenant) => {
                                                        const status =
                                                            LEASE_STATUS_MAP[
                                                                tenant
                                                                    .lease_status
                                                            ]
                                                        return (
                                                            <Box
                                                                key={
                                                                    tenant.lease_id
                                                                }
                                                                className={
                                                                    classes.tenantRow
                                                                }
                                                            >
                                                                <Checkbox
                                                                    size="small"
                                                                    color="primary"
                                                                    checked={selectedLeaseIds.includes(
                                                                        tenant.lease_id,
                                                                    )}
                                                                    onChange={() =>
                                                                        toggleTenant(
                                                                            tenant,
                                                                        )
                                                                    }
                                                                />
                                                                <Box flex={1}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        style={{
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {
                                                                            tenant.name
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                    >
                                                                        {
                                                                            tenant.unit_label
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                                {status && (
                                                                    <Chip
                                                                        size="small"
                                                                        label={
                                                                            status.label
                                                                        }
                                                                        style={{
                                                                            height: 20,
                                                                            fontSize:
                                                                                '0.65rem',
                                                                            backgroundColor: `${status.color}22`,
                                                                            color:
                                                                                status.color,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )
                                                    },
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box className={classes.footer}>
                            <Button
                                className={classes.cancelButton}
                                onClick={onClose}
                                disabled={isSending}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.primaryButton}
                                onClick={() => setConfirmOpen(true)}
                                disabled={!canSend || isSending || isDrafting}
                            >
                                {isSending ? 'Sending…' : 'Send Announcement'}
                            </Button>
                        </Box>
                    </Paper>
                </Slide>
            </Modal>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Send announcement?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will send &ldquo;{subject}&rdquo; to{' '}
                        {audienceType === 'ALL'
                            ? 'all tenants in the selected scope'
                            : `${selectedLeaseIds.length} selected tenant${
                                  selectedLeaseIds.length !== 1 ? 's' : ''
                              }`}
                        .
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        style={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmSend}
                        color="primary"
                        variant="contained"
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
